import { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Popover,
  InputBase,
  IconButton,
  Divider,
  styled,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useUsers } from "@/hooks/use-users";
import { useDebounce } from "@/hooks/debounce";

const SearchWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  height: 36,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  margin: theme.spacing(1),
}));

const StyledInputBase = styled(InputBase)(() => ({
  flex: 1,
  marginLeft: 4,
  fontSize: "0.875rem",
}));

const UserOption = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TriggerField = styled(Box, {
  shouldForwardProp: (p) => p !== "hasError" && p !== "isDisabled",
})(({ theme, hasError, isDisabled }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  border: `1px solid ${hasError ? theme.palette.error.main : "rgba(0,0,0,0.23)"}`,
  borderRadius: theme.shape.borderRadius,
  padding: "8.5px 14px",
  cursor: isDisabled ? "not-allowed" : "pointer",
  backgroundColor: isDisabled
    ? theme.palette.action.disabledBackground
    : "transparent",
  "&:hover": {
    borderColor: isDisabled ? undefined : theme.palette.text.primary,
  },
}));

export const FormUserSelectInput = ({ formik, name, label, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedLabel, setSelectedLabel] = useState("");

  const debouncedSearch = useDebounce(search, 500);

  const { data, isLoading } = useUsers(Boolean(anchorEl), {
    page,
    size: 10,
    search: debouncedSearch,
  });

  const users = data?.data ?? [];
  const totalCount = data?.total_count ?? 0;
  const totalPages = Math.ceil(totalCount / 10);

  const hasError = formik.touched[name] && Boolean(formik.errors[name]);
  const errorMsg = formik.touched[name] && formik.errors[name];

  const handleOpen = (e) => {
    if (disabled) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
    setPage(0);
    formik.setFieldTouched(name, true, true);
  };

  const handleSelect = (user) => {
    const fullName = [user.firstName, user.surname].filter(Boolean).join(" ");
    setSelectedLabel(`${fullName} — ${user.email}`);

    formik.setFieldValue(name, Number(user.userId), true);

    setAnchorEl(null);
    setSearch("");
    setPage(0);
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: hasError ? "error.main" : "text.secondary",
          mb: 0.5,
          display: "block",
          fontSize: "0.75rem",
        }}
      >
        {label}
      </Typography>

      <TriggerField
        hasError={hasError}
        isDisabled={disabled}
        onClick={handleOpen}
      >
        <Typography
          variant="body2"
          sx={{
            color: selectedLabel ? "text.primary" : "text.disabled",
            fontSize: "0.875rem",
            flex: 1,
          }}
        >
          {selectedLabel || `Select ${label}`}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "text.secondary" }} />
      </TriggerField>

      {errorMsg && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: "block", fontSize: "0.75rem" }}
        >
          {errorMsg}
        </Typography>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: anchorEl?.offsetWidth ?? 300,
              maxHeight: 360,
              display: "flex",
              flexDirection: "column",
              borderRadius: "8px",
              mt: 0.5,
            },
          },
        }}
      >
        <SearchWrapper>
          <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <StyledInputBase
            autoFocus
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
          {search && (
            <IconButton
              size="small"
              onClick={() => {
                setSearch("");
                setPage(0);
              }}
              sx={{ p: 0.25 }}
            >
              <ClearIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            </IconButton>
          )}
        </SearchWrapper>

        <Divider />

        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : users.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 2, textAlign: "center" }}
            >
              No users found
            </Typography>
          ) : (
            users.map((user) => (
              <UserOption key={user.userId} onClick={() => handleSelect(user)}>
                <Typography variant="body2" fontWeight={500}>
                  {[user.firstName, user.surname].filter(Boolean).join(" ") ||
                    "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email} · ID: {user.userId}
                </Typography>
              </UserOption>
            ))
          )}
        </Box>

        {totalPages > 1 && (
          <>
            <Divider />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 0.75,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Page {page + 1} of {totalPages}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    cursor: page > 0 ? "pointer" : "default",
                    color: page > 0 ? "primary.main" : "text.disabled",
                  }}
                  onClick={() => page > 0 && setPage((p) => p - 1)}
                >
                  Prev
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    cursor: page < totalPages - 1 ? "pointer" : "default",
                    color:
                      page < totalPages - 1 ? "primary.main" : "text.disabled",
                  }}
                  onClick={() => page < totalPages - 1 && setPage((p) => p + 1)}
                >
                  Next
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Popover>
    </Box>
  );
};
